import { useState } from 'react';
import {
  Box,
  Chip,
  LinearProgress,
  Paper,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Tab,
  Tabs,
  Typography,
} from '@mui/material';
import { useNavigate } from 'react-router-dom';
import {
  ManufacturingProduct,
  ManufacturingWorkCenter,
  OnshapeDocument,
  OnshapePart,
} from '../../types/manufacturing';

interface Props {
  products: ManufacturingProduct[];
  workCenters: ManufacturingWorkCenter[];
  onshapeDocuments: OnshapeDocument[];
  onshapeParts: OnshapePart[];
  loading: boolean;
}

type TabKey = 'products' | 'work_centers' | 'onshape_documents' | 'onshape_parts';

const TAB_DEFS: Array<{ key: TabKey; label: string }> = [
  { key: 'products', label: 'Products' },
  { key: 'work_centers', label: 'Work centers' },
  { key: 'onshape_documents', label: 'Onshape documents' },
  { key: 'onshape_parts', label: 'Onshape parts' },
];

function ProductsTable({ rows }: { rows: ManufacturingProduct[] }) {
  return (
    <TableContainer sx={{ maxHeight: '50vh' }}>
      <Table size="small" stickyHeader>
        <TableHead>
          <TableRow>
            <TableCell>Code</TableCell>
            <TableCell>Name</TableCell>
            <TableCell>Type</TableCell>
            <TableCell>UOM</TableCell>
            <TableCell>BOM?</TableCell>
            <TableCell>Active</TableCell>
          </TableRow>
        </TableHead>
        <TableBody>
          {rows.map((p) => (
            <TableRow key={p.id} hover>
              <TableCell sx={{ fontFamily: 'monospace' }}>{p.default_code ?? '—'}</TableCell>
              <TableCell>{p.name}</TableCell>
              <TableCell>{p.type ?? '—'}</TableCell>
              <TableCell>{p.uom ?? '—'}</TableCell>
              <TableCell>{p.is_manufactured ? '✓' : ''}</TableCell>
              <TableCell>{p.active ? '✓' : '✗'}</TableCell>
            </TableRow>
          ))}
        </TableBody>
      </Table>
    </TableContainer>
  );
}

function WorkCentersTable({ rows }: { rows: ManufacturingWorkCenter[] }) {
  return (
    <TableContainer sx={{ maxHeight: '50vh' }}>
      <Table size="small" stickyHeader>
        <TableHead>
          <TableRow>
            <TableCell>Code</TableCell>
            <TableCell>Name</TableCell>
            <TableCell>OEE target</TableCell>
            <TableCell>Active</TableCell>
          </TableRow>
        </TableHead>
        <TableBody>
          {rows.map((wc) => (
            <TableRow key={wc.id} hover>
              <TableCell sx={{ fontFamily: 'monospace' }}>{wc.code ?? '—'}</TableCell>
              <TableCell>{wc.name}</TableCell>
              <TableCell>{wc.oee_target != null ? `${wc.oee_target}%` : '—'}</TableCell>
              <TableCell>{wc.active ? '✓' : '✗'}</TableCell>
            </TableRow>
          ))}
        </TableBody>
      </Table>
    </TableContainer>
  );
}

function OnshapePartsTable({
  rows,
  onSelect,
}: {
  rows: OnshapePart[];
  onSelect: (partId: string) => void;
}) {
  return (
    <TableContainer sx={{ maxHeight: '50vh' }}>
      <Table size="small" stickyHeader>
        <TableHead>
          <TableRow>
            <TableCell>Part #</TableCell>
            <TableCell>Name</TableCell>
            <TableCell>Revision</TableCell>
            <TableCell>State</TableCell>
            <TableCell>Material</TableCell>
            <TableCell align="right">Mass (kg)</TableCell>
          </TableRow>
        </TableHead>
        <TableBody>
          {rows.map((p) => (
            <TableRow
              key={p.id}
              hover
              onClick={() => onSelect(p.id)}
              sx={{ cursor: 'pointer' }}
              data-testid={`manufacturing-part-row-${p.id}`}
            >
              <TableCell sx={{ fontFamily: 'monospace' }}>{p.part_number ?? '—'}</TableCell>
              <TableCell>{p.name}</TableCell>
              <TableCell>{p.revision ?? '—'}</TableCell>
              <TableCell>{p.state ?? '—'}</TableCell>
              <TableCell>{p.material ?? '—'}</TableCell>
              <TableCell align="right">{p.mass_kg != null ? p.mass_kg.toFixed(3) : '—'}</TableCell>
            </TableRow>
          ))}
        </TableBody>
      </Table>
    </TableContainer>
  );
}

function OnshapeDocumentsTable({ rows }: { rows: OnshapeDocument[] }) {
  return (
    <TableContainer sx={{ maxHeight: '50vh' }}>
      <Table size="small" stickyHeader>
        <TableHead>
          <TableRow>
            <TableCell>Name</TableCell>
            <TableCell>Onshape ID</TableCell>
            <TableCell>Owner</TableCell>
            <TableCell>Modified (remote)</TableCell>
          </TableRow>
        </TableHead>
        <TableBody>
          {rows.map((d) => (
            <TableRow key={d.id} hover>
              <TableCell>{d.name}</TableCell>
              <TableCell sx={{ fontFamily: 'monospace', fontSize: 11 }}>
                {d.onshape_id.slice(0, 12)}…
              </TableCell>
              <TableCell>
                {d.owner_type ? <Chip size="small" label={d.owner_type} /> : '—'}
              </TableCell>
              <TableCell>
                {d.modified_at_remote ? new Date(d.modified_at_remote).toLocaleDateString() : '—'}
              </TableCell>
            </TableRow>
          ))}
        </TableBody>
      </Table>
    </TableContainer>
  );
}

export function TruthTellerTables({
  products,
  workCenters,
  onshapeDocuments,
  onshapeParts,
  loading,
}: Props) {
  const navigate = useNavigate();
  const [tab, setTab] = useState<TabKey>('products');
  const counts: Record<TabKey, number> = {
    products: products.length,
    work_centers: workCenters.length,
    onshape_documents: onshapeDocuments.length,
    onshape_parts: onshapeParts.length,
  };

  return (
    <Paper variant="outlined" sx={{ p: 2, mb: 3 }} data-testid="manufacturing-tables">
      <Typography variant="h6" sx={{ mb: 1 }}>
        Tables
      </Typography>
      <Tabs
        value={tab}
        onChange={(_e, v) => setTab(v as TabKey)}
        sx={{ borderBottom: 1, borderColor: 'divider', mb: 1 }}
      >
        {TAB_DEFS.map((d) => (
          <Tab key={d.key} value={d.key} label={`${d.label} (${counts[d.key]})`} />
        ))}
      </Tabs>
      {/* Refresh indicator when data is already populated — without this,
          a refresh leaves stale rows visible with no feedback. */}
      {loading && counts[tab] > 0 && <LinearProgress sx={{ mb: 1 }} />}
      {loading && counts[tab] === 0 ? (
        <Box sx={{ p: 2 }}>
          <Typography variant="body2" color="text.secondary">
            Loading…
          </Typography>
        </Box>
      ) : counts[tab] === 0 ? (
        <Box sx={{ p: 2 }}>
          <Typography variant="body2" color="text.secondary">
            Empty. Run the matching snapshot script.
          </Typography>
        </Box>
      ) : tab === 'products' ? (
        <ProductsTable rows={products} />
      ) : tab === 'work_centers' ? (
        <WorkCentersTable rows={workCenters} />
      ) : tab === 'onshape_documents' ? (
        <OnshapeDocumentsTable rows={onshapeDocuments} />
      ) : (
        <OnshapePartsTable
          rows={onshapeParts}
          onSelect={(partId) => navigate(`/manufacturing/part/${encodeURIComponent(partId)}`)}
        />
      )}
    </Paper>
  );
}
